import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  input,
} from '@angular/core';
import { IAppPage } from 'src/app/models/navigation/navigation.interface';

@Component({
  selector: 'app-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss'],
  host: {
    '[class.expanded]': 'menuExpanded',
    '[class.extended]': 'menuExtended',
    '(focusin)': 'onMouseEnter()',
    '(mouseenter)': 'onMouseEnter()',
    '(focusout)': 'onMouseLeave()',
    '(mouseleave)': 'onMouseLeave()',
  },
})
export class MenuComponent implements AfterViewInit {
  @ViewChild('menuitems', { static: true }) private readonly _menuItems: ElementRef;

  public readonly appPages = input<IAppPage[]>();
  public readonly supportSectionRefPages = input<IAppPage[]>();

  @Output() public expansionChanged = new EventEmitter<void>();
  @Output() public menuStateChanged = new EventEmitter<{ isExpanded: boolean; isLocked: boolean }>();

  private _activeIndex = 0;
  @Input() public set activeIndex(index: number) {
    this._activeIndex = index;
    this._updateSelectedIndexBounds(this._activeIndex);
  }

  public selectedIndexTop = 0;
  public selectedIndexHeight = 0;
  public selectedIndexOpacity = 1;
  public menuExpanded = true;
  public menuExtended = true;
  private _temporarilyDisabled = false;

  constructor(private readonly _changeDetector: ChangeDetectorRef) {}

  public onMouseEnter = () => {
    if (!this.menuExtended && !this._temporarilyDisabled) {
      this.menuExpanded = true;
      this._emitMenuState();
    }
  };

  public onMouseLeave = () => {
    if (!this.menuExtended && !this._temporarilyDisabled) {
      this.menuExpanded = false;
      this.closeAllParentItems();
      this._emitMenuState();
    }
  };

  public ngAfterViewInit(): void {
    this._updateSelectedIndexBounds(this._activeIndex);
    this._changeDetector.detectChanges(); // Must be called to prevent NG0100 error
  }

  public toggleMenu() {
    this.menuExpanded = !this.menuExpanded;
    if (!this.menuExpanded) {
      this.closeAllParentItems();
    }
  }

  public extendMenu() {
    this.menuExtended = true;
    this.menuExpanded = true;
    this._emitMenuState();
  }

  public collapseMenu() {
    this.menuExtended = false;
    this.menuExpanded = false;
    this.closeAllParentItems();

    // Temporarily disable hover effects to ensure menu stays collapsed
    this._temporarilyDisabled = true;
    setTimeout(() => {
      this._temporarilyDisabled = false;
    }, 700); // Match the CSS transition duration

    this._emitMenuState();
    this._changeDetector.detectChanges(); // Force immediate DOM update
  }

  public toggleMenuExtension() {
    if (this.menuExtended) {
      this.collapseMenu();
    } else {
      this.extendMenu();
    }
  }

  public closeMenu() {
    if (this.menuExtended) {
      this.collapseMenu();
    }
    this.menuExpanded = false;
    this.closeAllParentItems();
    this._emitMenuState();
  }

  public updateSelection() {
    setTimeout(() => {
      this._updateSelectedIndexBounds(this._activeIndex);
      this._changeDetector.detectChanges();
    }, 100);
  }

  public onExpansionToggle(appPage: IAppPage): void {
    // Handle expansion toggle from nav-menu-item components
    this.expansionChanged.emit();
    this.updateSelection();
  }

  private _updateSelectedIndexBounds(index: number) {
    if (!this.appPages()?.length) return;

    const selectedItem = this._findSelectedMenuItem();

    if (selectedItem) {
      const menuRect = this._menuItems.nativeElement.getBoundingClientRect();
      const itemRect = selectedItem.getBoundingClientRect();

      this.selectedIndexTop = itemRect.top - menuRect.top;
      this.selectedIndexHeight = itemRect.height;
      this.selectedIndexOpacity = 1;
    } else {
      this.selectedIndexOpacity = 0;
    }
  }

  private _findSelectedMenuItem(): HTMLElement | null {
    if (!this.appPages()?.length) return null;

    try {
      const selectedItems = this._menuItems.nativeElement.querySelectorAll('app-nav-menu-item.selected');

      for (const selectedItem of selectedItems) {
        const isPageSelected = this._isCorrespondingPageSelected(selectedItem);
        if (isPageSelected) {
          const link = selectedItem.querySelector('.menu-item-link');
          if (link) {
            return link as HTMLElement;
          }

          const parentContainer = selectedItem.querySelector('.menu-item-parent');
          if (parentContainer) {
            return parentContainer as HTMLElement;
          }

          return selectedItem as HTMLElement;
        }
      }
    } catch (error) {
      console.warn('Error finding selected menu item:', error);
    }

    return null;
  }

  private _isCorrespondingPageSelected(menuItemElement: HTMLElement): boolean {
    const pages = this.appPages();
    if (!pages) return false;

    for (const appPage of pages) {
      if (appPage.selected) {
        return true;
      }
      if (appPage.children) {
        for (const child of appPage.children) {
          if (child.selected) {
            return true;
          }
        }
      }
    }
    return false;
  }

  private closeAllParentItems(): void {
    if (this.appPages()) {
      this.appPages()?.forEach((page) => {
        if (page.isParent) {
          page.isExpanded = false;
        }
      });
    }
    const supportPages = this.supportSectionRefPages();
    if (supportPages) {
      supportPages.forEach((page) => {
        if (page.isParent) {
          page.isExpanded = false;
        }
      });
    }
  }

  private _emitMenuState() {
    this.menuStateChanged.emit({
      isExpanded: this.menuExpanded,
      isLocked: this.menuExtended,
    });
  }
}
